import { createContext, useContext, useId } from "react";
import { useTranslation } from "../i18n/useTranslation";

/**
 * THE SESSION ROW (designer, lura-web-v3 "Oct 7 · Sign out (Book)").
 *
 * Found in the rc.3 joint session: a signed-in client had no way to sign
 * out, so on a shared device the next person stayed signed in with the
 * previous client's name and email prefilled. The footer now NAMES the
 * active account ("Signed in as <email>") with Sign out beside it, above
 * the language and legal row, so the next person sees whose session it is.
 *
 * It renders only where a screen provides it (SignOutContext): Book's flow
 * and the account-gated profile, for a signed-in client, and never while a
 * booking is sending or its result is unknown. ABSENT, not disabled,
 * otherwise, so it is never a dead control and never ends a session under a
 * booking that may exist.
 */
export interface SessionRowValue {
  /** The signed-in account's email, as the account read returned it. */
  email: string;
  signOut(): void;
}

export const SignOutContext = createContext<SessionRowValue | null>(null);

export function SessionRow() {
  const session = useContext(SignOutContext);
  const { t } = useTranslation();
  const whoId = useId();
  if (!session) return null;
  return (
    <div className="v3-session-row" data-testid="session-row">
      <p className="v3-session-who" id={whoId} data-testid="session-who">
        {t("flow.session.signedInAs")} <b>{session.email}</b>
      </p>
      <button
        type="button"
        className="v3-session-signout"
        data-testid="flow-sign-out"
        aria-describedby={whoId}
        onClick={session.signOut}
      >
        {t("flow.signOut")}
      </button>
    </div>
  );
}
